export type AuditLogCategory =
  | 'lifecycle'
  | 'configuration'
  | 'channels'
  | 'access_control'
  | 'infrastructure'
  | 'credentials'
  | 'integrations'
  | 'ad_accounts'
  | 'agent_actions'
  | 'artifacts'
  | 'business_processes'
  | 'staff'

export const CATEGORY_BY_ACTION: Record<string, AuditLogCategory> = {
  'worker.created': 'lifecycle',
  'worker.provisioning_started': 'lifecycle',
  'worker.provisioned': 'lifecycle',
  'worker.provisioning_failed': 'lifecycle',
  'worker.whatsapp_config_failed': 'lifecycle',
  'worker.provisioning_retried': 'lifecycle',
  'worker.destroyed': 'lifecycle',
  'worker.profile_updated': 'configuration',
  'worker.avatar_generated': 'configuration',
  'worker.google_accounts_updated': 'configuration',
  'worker.meta_accounts_updated': 'configuration',
  'worker.whatsapp_connected': 'channels',
  'worker.whatsapp_removed': 'channels',
  'worker.whatsapp_provider_changed': 'channels',
  'worker.discord_connected': 'channels',
  'worker.discord_removed': 'channels',
  'worker.telegram_connected': 'channels',
  'worker.telegram_removed': 'channels',
  'worker.allowlist_entry_added': 'access_control',
  'worker.allowlist_entry_removed': 'access_control',
  'worker.dm_policy_changed': 'access_control',
  'worker.credentials_synced': 'infrastructure',
  'worker.vault_synced': 'infrastructure',
  'worker.linked': 'infrastructure',
  'worker.gws_provisioned': 'infrastructure',
  'worker.phone_assigned': 'infrastructure',
  'worker.fleet_operation': 'infrastructure',
  'credential.created': 'credentials',
  'credential.updated': 'credentials',
  'credential.deleted': 'credentials',
  'credential.assigned_to_worker': 'credentials',
  'credential.unassigned_from_worker': 'credentials',
  'integration.connected': 'integrations',
  'integration.disconnected': 'integrations',
  'integration.assigned_to_worker': 'integrations',
  'integration.unassigned_from_worker': 'integrations',
  'integration.notion_status_toggled': 'integrations',
  'integration.slack_channel_updated': 'integrations',
  'integration.access_granted': 'integrations',
  'integration.access_updated': 'integrations',
  'integration.access_revoked': 'integrations',
  'ad_account.imported': 'ad_accounts',
  'ad_account.removed': 'ad_accounts',
  'ad_account.updated': 'ad_accounts',
  'agent.tool_call.exec': 'agent_actions',
  'agent.tool_call.read': 'agent_actions',
  'agent.tool_call.write': 'agent_actions',
  'agent.tool_call.edit': 'agent_actions',
  'agent.tool_call.browser': 'agent_actions',
  'agent.tool_call.other': 'agent_actions',
  'artifact.published': 'artifacts',
  'artifact.archived': 'artifacts',
  'artifact.destroyed': 'artifacts',
  'business_process.cron_linked': 'business_processes',
  'business_process.cron_unlinked': 'business_processes',
  'staff.agent_chat.started': 'staff',
}

// Iteração 1 (I4.1): o DS reserva cor para STATUS — categoria de evento não é
// status, então o arco-íris de paleta crua (amber/blue/red/teal/…-100) virou
// chip neutro e a diferenciação fica no rótulo. O mapa permanece para poder
// re-diferenciar por token no futuro sem tocar os call sites.
const COLOR_BY_CATEGORY: Record<AuditLogCategory, string> = {
  lifecycle: 'bg-secondary text-muted-foreground',
  configuration: 'bg-secondary text-muted-foreground',
  channels: 'bg-secondary text-muted-foreground',
  access_control: 'bg-secondary text-muted-foreground',
  infrastructure: 'bg-secondary text-muted-foreground',
  credentials: 'bg-secondary text-muted-foreground',
  integrations: 'bg-secondary text-muted-foreground',
  ad_accounts: 'bg-secondary text-muted-foreground',
  agent_actions: 'bg-secondary text-muted-foreground',
  artifacts: 'bg-secondary text-muted-foreground',
  business_processes: 'bg-secondary text-muted-foreground',
  staff: 'bg-secondary text-muted-foreground',
}

const LABEL_BY_CATEGORY: Record<AuditLogCategory, string> = {
  lifecycle: 'Ciclo de vida',
  configuration: 'Configuração',
  channels: 'Canais',
  access_control: 'Controle de acesso',
  infrastructure: 'Infraestrutura',
  credentials: 'Credenciais',
  integrations: 'Integrações',
  ad_accounts: 'Contas de anúncio',
  agent_actions: 'Ações do agente',
  artifacts: 'Artefatos',
  business_processes: 'Processos',
  staff: 'Equipe StationZero',
}

export function auditLogCategory(action: string): AuditLogCategory {
  return CATEGORY_BY_ACTION[action] || 'configuration'
}

export function auditLogBadgeClass(action: string): string {
  return COLOR_BY_CATEGORY[auditLogCategory(action)]
}

export function auditLogCategoryLabel(category: AuditLogCategory): string {
  return LABEL_BY_CATEGORY[category]
}

export function auditLogLabel(action: string): string {
  return action.split('.').join(' / ')
}

export const CATEGORY_ORDER: AuditLogCategory[] = [
  'lifecycle', 'configuration', 'channels', 'access_control',
  'infrastructure', 'credentials', 'integrations', 'ad_accounts',
  'agent_actions', 'artifacts', 'business_processes', 'staff',
]

export const ACTIONS_BY_CATEGORY: Record<AuditLogCategory, string[]> = {} as Record<AuditLogCategory, string[]>
for (const [action, category] of Object.entries(CATEGORY_BY_ACTION)) {
  if (!ACTIONS_BY_CATEGORY[category]) ACTIONS_BY_CATEGORY[category] = []
  ACTIONS_BY_CATEGORY[category].push(action)
}

export const ALL_ACTIONS: string[] = CATEGORY_ORDER.flatMap((cat) => ACTIONS_BY_CATEGORY[cat])

export const DEFAULT_EXCLUDED_ACTIONS: string[] = ACTIONS_BY_CATEGORY.agent_actions

export function buildExcludedParam(excludedActions: string[]): string | string[] {
  return excludedActions.length === 0 ? 'none' : excludedActions
}
